/**
 * BatteryConfiguration - Energy storage configuration component
 *
 * Handles battery sizing, costs, charge/discharge settings, and optimization.
 * Supports database selection (multi-battery with quantities) and manual input.
 *
 * Supports two modes:
 * 1. Props mode (legacy): Pass individual values and setters as props
 * 2. Context mode (new): Uses React Hook Form context when available
 *
 * The component automatically detects which mode to use based on whether
 * it's wrapped in a FormProvider and whether props are provided.
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Autocomplete,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  IconButton,
  Button,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Delete, Add } from '@mui/icons-material';
import { vortxColors, useEquipmentDatabase } from '@vortx/shared-ui';
import { useFormField, useFormArrayField } from './useFormField';

const SIZING_MODES = [
  { value: 'prescribed', label: 'Prescribed Size', description: 'Specify exact battery size' },
  { value: 'optimal', label: 'Optimize Size', description: 'Let REopt find optimal battery size' },
];

const CHARGE_SOURCES = [
  { value: 'pv_and_grid', label: 'PV and Grid', description: 'Battery can charge from solar and grid' },
  { value: 'pv_only', label: 'PV Only', description: 'Battery can only charge from solar' },
];

const DURATION_OPTIONS = [
  { value: 1, label: '1 Hour' },
  { value: 2, label: '2 Hour' },
  { value: 4, label: '4 Hour' },
];

/**
 * BatteryConfiguration Component
 *
 * @param {Object} props
 * @param {boolean} [props.useStorage] - Whether storage is enabled (props mode)
 * @param {Function} [props.setUseStorage] - Storage enabled setter (props mode)
 * @param {string} [props.sizingMode] - 'optimal' or 'prescribed' (props mode)
 * @param {Function} [props.setSizingMode] - Sizing mode setter (props mode)
 * @param {Array} [props.batteries] - Array of battery configs (props mode)
 * @param {Function} [props.setBatteries] - Batteries array setter (props mode)
 * @param {string} [props.storageMaxKw] - Max storage kW (props mode)
 * @param {Function} [props.setStorageMaxKw] - Storage kW setter (props mode)
 * @param {string} [props.storageMaxKwh] - Max storage kWh (props mode)
 * @param {Function} [props.setStorageMaxKwh] - Storage kWh setter (props mode)
 * @param {boolean} [props.useGenLoadBank] - Generator load bank charging (props mode)
 * @param {Function} [props.setUseGenLoadBank] - Gen load bank setter (props mode)
 * @param {string} [props.batteryReservePercent] - Reserve SOC percent (props mode)
 * @param {Function} [props.setBatteryReservePercent] - Reserve percent setter (props mode)
 * @param {string} [props.bessChargeSource] - Charge source (props mode)
 * @param {Function} [props.setBessChargeSource] - Charge source setter (props mode)
 * @param {string} [props.optimizationEngine] - Optimization engine type
 * @param {Object} [props.vortxSettings] - Vortx-specific settings
 * @param {Function} [props.setVortxSettings] - Vortx settings setter
 * @param {Object} [props.vortxStyles] - Styling object
 * @param {Function} [props.onBatterySelect] - Callback for battery selection (receives array)
 */
function BatteryConfiguration({
  // Props mode values (backward compatible)
  useStorage: propUseStorage,
  setUseStorage,
  sizingMode: propSizingMode = 'prescribed',
  setSizingMode,
  batteries: propBatteries,
  setBatteries,
  storageMaxKw: propStorageMaxKw,
  setStorageMaxKw,
  storageMaxKwh: propStorageMaxKwh,
  setStorageMaxKwh,
  useGenLoadBank: propUseGenLoadBank,
  setUseGenLoadBank,
  batteryReservePercent: propBatteryReservePercent,
  setBatteryReservePercent,
  bessChargeSource: propBessChargeSource,
  setBessChargeSource,
  // Common props (not in form context)
  optimizationEngine,
  vortxSettings,
  setVortxSettings,
  vortxStyles = {},
  onBatterySelect,
}) {
  // Fetch battery equipment from database
  const { data: batteryDatabase = [], loading: loadingBatteries } = useEquipmentDatabase('bess');

  // Get form fields (automatically uses context or props)
  const enabled = useFormField('battery.enabled', propUseStorage, setUseStorage);
  const sizingMode = useFormField('battery.sizingMode', propSizingMode, setSizingMode);
  const maxKw = useFormField('battery.maxKw', propStorageMaxKw, setStorageMaxKw);
  const maxKwh = useFormField('battery.maxKwh', propStorageMaxKwh, setStorageMaxKwh);
  const reservePercent = useFormField('battery.reservePercent', propBatteryReservePercent, setBatteryReservePercent);
  const chargeSource = useFormField('battery.chargeSource', propBessChargeSource, setBessChargeSource);
  const batteries = useFormArrayField('battery.batteries', propBatteries, setBatteries);

  // Local state for input mode and reserve unit
  // Auto-detect database mode if batteries array has entries from a previous session
  const [inputMode, setInputMode] = useState(() =>
    (propBatteries?.length > 0 || batteries.value?.length > 0) ? 'database' : 'manual'
  ); // 'database' or 'manual'
  const [reserveUnit, setReserveUnit] = useState('percent'); // 'percent' or 'kwh'

  // Calculate derived values
  const capacityKwh = parseFloat(maxKwh.value) || 0;
  const capacityKw = parseFloat(maxKw.value) || 0;

  // Calculate duration from stored kWh/kW ratio, default to 2 if can't calculate
  const calculatedDuration = capacityKw > 0 ? Math.round(capacityKwh / capacityKw) : 2;
  // Snap to nearest valid duration option (1, 2, or 4)
  const duration = [1, 2, 4].includes(calculatedDuration) ? calculatedDuration :
    calculatedDuration <= 1.5 ? 1 :
    calculatedDuration <= 3 ? 2 : 4;
  const reservePercentValue = parseFloat(reservePercent.value) || 20;
  const reserveKwh = (reservePercentValue / 100) * capacityKwh;

  // Calculate totals from batteries array (database mode)
  const batteryTotals = useMemo(() => {
    const items = batteries.value || [];
    const totalKw = items.reduce((sum, b) => sum + ((parseFloat(b.kw) || 0) * (parseInt(b.qty, 10) || 1)), 0);
    const totalKwh = items.reduce((sum, b) => sum + ((parseFloat(b.kwh) || 0) * (parseInt(b.qty, 10) || 1)), 0);
    return { totalKw, totalKwh };
  }, [batteries.value]);

  // Sync batteries array totals → maxKw/maxKwh and notify parent
  const syncBatteriesToForm = useCallback((batteryList) => {
    const totalKw = batteryList.reduce((sum, b) => sum + ((parseFloat(b.kw) || 0) * (parseInt(b.qty, 10) || 1)), 0);
    const totalKwh = batteryList.reduce((sum, b) => sum + ((parseFloat(b.kwh) || 0) * (parseInt(b.qty, 10) || 1)), 0);
    maxKw.setValue(String(totalKw));
    maxKwh.setValue(String(totalKwh));

    // Auto-derive max_cycles_per_year from first battery's warranty when user hasn't overridden
    if (batteryList.length > 0 && setVortxSettings && !vortxSettings?.batteryMaxCyclesPerYear) {
      const first = batteryList[0];
      const dbBatt = batteryDatabase.find(b => b.id === first.id);
      if (dbBatt) {
        // Firestore uses snake_case (cycle_life, warranty_years); static JSON uses camelCase (cycleLife, warrantyYears)
        const cycleLife = dbBatt.cycle_life || dbBatt.cycleLife || 0;
        const warrantyYears = dbBatt.warranty_years || dbBatt.warrantyYears || 0;
        if (cycleLife > 0 && warrantyYears > 0) {
          const derived = Math.round(cycleLife / warrantyYears);
          setVortxSettings(prev => ({ ...prev, batteryMaxCyclesPerYear: String(derived) }));
        }
      }
    }

    if (onBatterySelect) {
      onBatterySelect(batteryList.length > 0 ? batteryList : null);
    }
  }, [maxKw, maxKwh, onBatterySelect, batteryDatabase, vortxSettings?.batteryMaxCyclesPerYear, setVortxSettings]);

  // Enrich saved battery entries with cost data from equipment database on load
  // Saved entries only have {id, qty} — need to look up full specs + costs
  const [hasEnrichedBatteries, setHasEnrichedBatteries] = useState(false);
  useEffect(() => {
    if (hasEnrichedBatteries || loadingBatteries || batteryDatabase.length === 0) return;
    const items = batteries.value || [];
    if (items.length === 0) return;
    // Check if enrichment is needed (entries missing cost data)
    const needsEnrich = items.some(b => !b.cost_per_kwh && !b.equipment_cost);
    if (!needsEnrich) {
      setHasEnrichedBatteries(true);
      return;
    }
    const enriched = items.map(entry => {
      const dbBatt = batteryDatabase.find(eq => eq.id === entry.id);
      if (!dbBatt) return entry;
      const kwh = dbBatt.capacity_kwh || 0;
      return {
        ...entry,
        manufacturer: dbBatt.manufacturer,
        model: dbBatt.model,
        kw: dbBatt.power_kw,
        kwh,
        cost_per_kwh: dbBatt.cost_per_kwh ?? (dbBatt.equipment_cost && kwh ? dbBatt.equipment_cost / kwh : 0),
        cost_per_kw: dbBatt.cost_per_kw ?? (dbBatt.equipment_cost && dbBatt.power_kw ? dbBatt.equipment_cost / dbBatt.power_kw : 0),
        install_cost_per_kwh: dbBatt.install_cost_per_kwh ?? (dbBatt.installation_cost && kwh ? dbBatt.installation_cost / kwh : 0),
        equipment_cost: dbBatt.equipment_cost,
        installation_cost: dbBatt.installation_cost,
      };
    });
    batteries.setValue(enriched);
    syncBatteriesToForm(enriched);
    setHasEnrichedBatteries(true);
  }, [batteryDatabase, loadingBatteries, batteries, hasEnrichedBatteries, syncBatteriesToForm]);

  // Add a battery from database selection
  const handleAddBattery = useCallback((event, battery) => {
    if (!battery) return;
    // Check if this model is already in the list
    const existing = (batteries.value || []).find(b => b.id === battery.id);
    let updated;
    if (existing) {
      // Increment quantity
      updated = (batteries.value || []).map(b =>
        b.id === battery.id ? { ...b, qty: (parseInt(b.qty, 10) || 1) + 1 } : b
      );
    } else {
      // Compute per-kWh rates: prefer direct fields, fall back to total / capacity
      const kwh = battery.capacity_kwh || 0;
      const costPerKwh = battery.cost_per_kwh
        ?? (battery.equipment_cost && kwh ? battery.equipment_cost / kwh : 0);
      const installPerKwh = battery.install_cost_per_kwh
        ?? (battery.installation_cost && kwh ? battery.installation_cost / kwh : 0);
      const costPerKw = battery.cost_per_kw
        ?? (battery.equipment_cost && battery.power_kw ? battery.equipment_cost / battery.power_kw : 0);

      const newEntry = {
        id: battery.id,
        manufacturer: battery.manufacturer,
        model: battery.model,
        kw: battery.power_kw,
        kwh,
        qty: 1,
        cost_per_kwh: costPerKwh,
        cost_per_kw: costPerKw,
        install_cost_per_kwh: installPerKwh,
      };
      updated = [...(batteries.value || []), newEntry];
    }
    batteries.setValue(updated);
    syncBatteriesToForm(updated);
  }, [batteries, syncBatteriesToForm]);

  // Update quantity for a battery row
  const handleQtyChange = useCallback((id, newQty) => {
    const qty = Math.max(1, parseInt(newQty, 10) || 1);
    const updated = (batteries.value || []).map(b =>
      b.id === id ? { ...b, qty } : b
    );
    batteries.setValue(updated);
    syncBatteriesToForm(updated);
  }, [batteries, syncBatteriesToForm]);

  // Remove a battery row
  const handleRemoveBattery = useCallback((id) => {
    const updated = (batteries.value || []).filter(b => b.id !== id);
    batteries.setValue(updated);
    syncBatteriesToForm(updated);
  }, [batteries, syncBatteriesToForm]);

  // Handle power input change (updates kWh based on duration)
  const handlePowerChange = (value) => {
    maxKw.setValue(value);
    const power = parseFloat(value) || 0;
    maxKwh.setValue(String(power * duration));
  };

  // Handle duration change (updates kWh, duration is calculated from kWh/kW ratio)
  const handleDurationChange = (event, newDuration) => {
    if (newDuration !== null) {
      const power = parseFloat(maxKw.value) || 0;
      maxKwh.setValue(String(power * newDuration));
    }
  };

  // Handle reserve change based on unit
  const handleReserveChange = (event, value) => {
    if (reserveUnit === 'percent') {
      reservePercent.setValue(String(value));
    } else {
      // Converting kWh back to percent
      const percent = capacityKwh > 0 ? (value / capacityKwh) * 100 : 0;
      reservePercent.setValue(String(Math.round(percent)));
    }
  };

  // Get slider value based on unit
  const sliderValue = reserveUnit === 'percent' ? reservePercentValue : Math.round(reserveKwh);
  const sliderMax = reserveUnit === 'percent' ? 50 : Math.round(capacityKwh * 0.5);

  // Filter out already-selected batteries from autocomplete options
  const availableBatteries = useMemo(() => {
    const selectedIds = new Set((batteries.value || []).map(b => b.id));
    return batteryDatabase.filter(b => !selectedIds.has(b.id));
  }, [batteryDatabase, batteries.value]);

  return (
    <Box sx={{
      border: `1px solid rgba(178, 140, 80, 0.2)`,
      borderRadius: 2,
      overflow: 'hidden',
      mb: 2
    }}>
      {/* Header with toggle */}
      <Box sx={{
        p: 2,
        bgcolor: enabled.value ? alpha(vortxColors.gold, 0.1) : 'background.paper',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(enabled.value)}
              onChange={(e) => enabled.setValue(e.target.checked)}
              sx={{ color: vortxColors.gold, '&.Mui-checked': { color: vortxColors.gold } }}
            />
          }
          label={
            <Typography sx={{ fontWeight: 500, color: 'text.primary' }}>
              Battery Energy Storage
            </Typography>
          }
        />
        {enabled.value && capacityKw > 0 && (
          <Chip
            label={`${capacityKw.toLocaleString()} kW / ${capacityKwh.toLocaleString()} kWh`}
            size="small"
            sx={{ bgcolor: alpha(vortxColors.gold, 0.15), color: 'text.primary' }}
          />
        )}
      </Box>

      {/* Configuration section */}
      {enabled.value && (
        <Box sx={{ p: 2, borderTop: `1px solid rgba(178, 140, 80, 0.2)` }}>
          {/* Sizing Mode */}
          <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
            <FormLabel component="legend" sx={{ color: 'text.primary', fontWeight: 500, mb: 1 }}>
              Sizing Mode
            </FormLabel>
            <RadioGroup
              row
              value={sizingMode.value || 'prescribed'}
              onChange={(e) => sizingMode.setValue(e.target.value)}
            >
              {SIZING_MODES.map((mode) => (
                <FormControlLabel
                  key={mode.value}
                  value={mode.value}
                  control={<Radio sx={{ color: vortxColors.gold, '&.Mui-checked': { color: vortxColors.gold } }} />}
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>{mode.label}</Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{mode.description}</Typography>
                    </Box>
                  }
                />
              ))}
            </RadioGroup>
          </FormControl>

          {/* Input Mode Toggle */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ color: 'text.primary', mb: 1, fontWeight: 500 }}>
              Battery Selection
            </Typography>
            <ToggleButtonGroup
              value={inputMode}
              exclusive
              onChange={(e, mode) => mode && setInputMode(mode)}
              size="small"
              sx={{ mb: 2 }}
            >
              <ToggleButton value="database" sx={{ px: 2 }}>
                Select from Database
              </ToggleButton>
              <ToggleButton value="manual" sx={{ px: 2 }}>
                Manual Input
              </ToggleButton>
            </ToggleButtonGroup>

            {/* Database Selection — Multi-battery with quantities */}
            {inputMode === 'database' && (
              <Box>
                {/* Selected batteries list */}
                {(batteries.value || []).length > 0 && (
                  <Box sx={{
                    mb: 2,
                    border: `1px solid rgba(178, 140, 80, 0.15)`,
                    borderRadius: 1,
                    overflow: 'hidden',
                  }}>
                    {(batteries.value || []).map((b) => {
                      const qty = parseInt(b.qty, 10) || 1;
                      const rowKw = (parseFloat(b.kw) || 0) * qty;
                      const rowKwh = (parseFloat(b.kwh) || 0) * qty;
                      return (
                        <Box
                          key={b.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            px: 2,
                            py: 1,
                            borderBottom: `1px solid rgba(178, 140, 80, 0.1)`,
                            '&:last-child': { borderBottom: 'none' },
                          }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }} noWrap>
                              {b.manufacturer} {b.model}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              {rowKw.toLocaleString()} kW / {rowKwh.toLocaleString()} kWh
                            </Typography>
                          </Box>
                          <TextField
                            type="number"
                            value={qty}
                            onChange={(e) => handleQtyChange(b.id, e.target.value)}
                            inputProps={{ min: 1, max: 99, style: { textAlign: 'center' } }}
                            size="small"
                            sx={{ width: 72 }}
                            label="Qty"
                            InputLabelProps={{ shrink: true }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveBattery(b.id)}
                            sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      );
                    })}
                    {/* Totals row */}
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      px: 2,
                      py: 1,
                      bgcolor: alpha(vortxColors.gold, 0.08),
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        Total
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: vortxColors.gold }}>
                        {batteryTotals.totalKw.toLocaleString()} kW / {batteryTotals.totalKwh.toLocaleString()} kWh
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Add battery autocomplete */}
                <Autocomplete
                  options={availableBatteries}
                  getOptionLabel={(option) => `${option.manufacturer} ${option.model} (${option.power_kw} kW / ${option.capacity_kwh} kWh)`}
                  groupBy={(option) => option.category}
                  value={null}
                  onChange={handleAddBattery}
                  loading={loadingBatteries}
                  blurOnSelect
                  clearOnBlur
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={(batteries.value || []).length > 0 ? 'Add Another Battery' : 'Select Battery'}
                      placeholder="Search batteries..."
                      size="small"
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Add fontSize="small" sx={{ color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                            {option.manufacturer} {option.model}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {option.power_kw} kW / {option.capacity_kwh} kWh / {(option.capacity_kwh / option.power_kw).toFixed(1)}hr
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}
                />
              </Box>
            )}

            {/* Manual Input */}
            {inputMode === 'manual' && (
              <Box>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <TextField
                    label={sizingMode.value === 'prescribed' ? 'Power Capacity (kW)' : 'Max Power Capacity (kW)'}
                    type="number"
                    value={maxKw.value || ''}
                    onChange={(e) => handlePowerChange(e.target.value)}
                    inputProps={{ min: 0 }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ ...vortxStyles.textField, flex: 1 }}
                    helperText={sizingMode.value === 'optimal' ? 'Maximum allowed size for optimization' : 'Exact discharge power to install'}
                    error={!!maxKw.error}
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ color: 'text.primary', mb: 1 }}>
                    Duration
                  </Typography>
                  <ToggleButtonGroup
                    value={duration}
                    exclusive
                    onChange={handleDurationChange}
                    size="small"
                  >
                    {DURATION_OPTIONS.map((opt) => (
                      <ToggleButton
                        key={opt.value}
                        value={opt.value}
                        sx={{ px: 3 }}
                      >
                        {opt.label}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                    Energy Capacity: {capacityKwh.toLocaleString()} kWh ({duration} hours x {capacityKw} kW)
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          {/* Reserve SOC Slider with Unit Toggle */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                Minimum State of Charge (Reserve)
              </Typography>
              <ToggleButtonGroup
                value={reserveUnit}
                exclusive
                onChange={(e, unit) => unit && setReserveUnit(unit)}
                size="small"
              >
                <ToggleButton value="percent" sx={{ py: 0.25, px: 1.5, fontSize: '0.75rem' }}>
                  %
                </ToggleButton>
                <ToggleButton value="kwh" sx={{ py: 0.25, px: 1.5, fontSize: '0.75rem' }}>
                  kWh
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Prominent Value Display */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'baseline',
              gap: 1,
              mb: 1,
              py: 1,
              bgcolor: alpha(vortxColors.gold, 0.08),
              borderRadius: 1
            }}>
              <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {reserveUnit === 'percent' ? `${reservePercentValue}%` : `${Math.round(reserveKwh)}`}
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                {reserveUnit === 'percent'
                  ? `(${Math.round(reserveKwh)} kWh)`
                  : `kWh (${reservePercentValue.toFixed(0)}%)`
                }
              </Typography>
            </Box>

            <Slider
              value={sliderValue}
              onChange={handleReserveChange}
              min={0}
              max={sliderMax || 50}
              step={1}
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => reserveUnit === 'percent' ? `${v}%` : `${v} kWh`}
              sx={{
                color: vortxColors.gold,
                '& .MuiSlider-markLabel': { color: 'text.primary' }
              }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Usable: {(capacityKwh - reserveKwh).toFixed(0)} kWh ({(100 - reservePercentValue).toFixed(0)}%)
              </Typography>
            </Box>
          </Box>

          {/* Charge Source */}
          <TextField
            select
            label="Charge Source"
            value={chargeSource.value || 'pv_and_grid'}
            onChange={(e) => chargeSource.setValue(e.target.value)}
            fullWidth
            sx={{ ...vortxStyles.textField, mb: 2 }}
            error={!!chargeSource.error}
          >
            {CHARGE_SOURCES.map((source) => (
              <MenuItem key={source.value} value={source.value}>
                <Box>
                  <Typography variant="body2" sx={{ color: 'text.primary' }}>{source.label}</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>{source.description}</Typography>
                </Box>
              </MenuItem>
            ))}
          </TextField>

          {/* Generator load bank option - keeping as prop-based for now */}
          {propUseGenLoadBank !== undefined && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={propUseGenLoadBank}
                  onChange={(e) => setUseGenLoadBank && setUseGenLoadBank(e.target.checked)}
                  sx={{ color: vortxColors.gold, '&.Mui-checked': { color: vortxColors.gold } }}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ color: 'text.primary' }}>Enable Generator Load Bank Charging</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Allow generators to charge battery when economical
                  </Typography>
                </Box>
              }
            />
          )}

          {/* Battery degradation and lifecycle (Vortx Flux Only) */}
          {optimizationEngine === 'vortx_flux' && (() => {
            // Derive max cycles/year from first selected battery's warranty
            const firstBatt = (batteries.value || [])[0];
            const dbEntry = firstBatt ? batteryDatabase.find(b => b.id === firstBatt.id) : null;
            // Firestore uses snake_case (cycle_life, warranty_years); static JSON uses camelCase (cycleLife, warrantyYears)
            const cycleLife = dbEntry?.cycle_life || dbEntry?.cycleLife || 0;
            const warrantyYears = dbEntry?.warranty_years || dbEntry?.warrantyYears || 0;
            const derivedCycles = (cycleLife > 0 && warrantyYears > 0) ? Math.round(cycleLife / warrantyYears) : null;
            const cyclesHelperText = derivedCycles
              ? `From warranty: ${cycleLife} cycles / ${warrantyYears} yr = ${derivedCycles}/yr`
              : 'Warranty cycle limit per year';

            return (
              <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                <Typography variant="subtitle2" sx={{ mb: 2, color: vortxColors.gold }}>
                  Battery Lifecycle (Vortx Flux)
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      label="Cycle Cost ($/kWh)"
                      type="number"
                      value={vortxSettings?.batteryCycleCostPerKwh || ''}
                      onChange={(e) => setVortxSettings(prev => ({ ...prev, batteryCycleCostPerKwh: e.target.value }))}
                      helperText="Degradation cost per throughput"
                      fullWidth
                      sx={vortxStyles.textField}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      label="Max Cycles/Year"
                      type="number"
                      value={vortxSettings?.batteryMaxCyclesPerYear || ''}
                      onChange={(e) => setVortxSettings(prev => ({ ...prev, batteryMaxCyclesPerYear: e.target.value }))}
                      placeholder={derivedCycles ? String(derivedCycles) : '280'}
                      helperText={cyclesHelperText}
                      fullWidth
                      sx={vortxStyles.textField}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Box>
                </Box>
              </Box>
            );
          })()}
        </Box>
      )}
    </Box>
  );
}

// Export constants for use elsewhere
export { SIZING_MODES, CHARGE_SOURCES, DURATION_OPTIONS };

export default BatteryConfiguration;
